import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataTable, Pagination, LinkCell, Select, formatDate, type Column } from '@datahub/design-system'
import { get } from '../../lib/api'
import { ListPage } from '../AdminShell'
import { StatusTag } from '../../components/StatusTag'

interface SubmissionRow {
  id: string
  reference: string
  version: number
  submittedAt: string
  submittedByName: string
  company: { id: string; displayName: string }
  periodLabel: string
  rowCounts: Record<string, number>
  confidentialCounts: Record<string, number>
  completionScores: Record<string, { pct: number }>
  copiedToCoreStatus: string
}

export function Submissions() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const copied = params.get('copied') ?? ''

  const { data, isLoading } = useQuery({
    queryKey: ['submissions', copied, page],
    queryFn: () =>
      get<{ total: number; items: SubmissionRow[] }>(
        `/admin/submissions?${new URLSearchParams({ ...(copied ? { copied } : {}), page: String(page), pageSize: '25' })}`,
      ),
  })

  const totalRows = (r: SubmissionRow) => Object.values(r.rowCounts ?? {}).reduce((a, b) => a + b, 0)
  const overall = (r: SubmissionRow) => {
    const pcts = Object.values(r.completionScores ?? {}).map((s) => s.pct)
    return pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : 0
  }

  const columns: Column<SubmissionRow>[] = [
    { key: 'company', header: 'Company', width: 240, sticky: true,
      render: (s) => <LinkCell onClick={() => navigate(`/admin/submissions/${s.id}`)}>{s.company.displayName}</LinkCell> },
    { key: 'period', header: 'Period', width: 130, render: (s) => s.periodLabel },
    { key: 'reference', header: 'Reference', width: 190, render: (s) => s.reference },
    { key: 'version', header: 'Version', width: 90, align: 'right', render: (s) => String(s.version) },
    { key: 'submitted', header: 'Submitted', width: 140, render: (s) => formatDate(s.submittedAt) },
    { key: 'by', header: 'Submitted by', width: 200, render: (s) => s.submittedByName },
    { key: 'completion', header: 'Completion', width: 110, align: 'right', render: (s) => `${overall(s)}%` },
    { key: 'rows', header: 'Rows', width: 90, align: 'right', render: (s) => String(totalRows(s)) },
    { key: 'confidential', header: 'Confidential', width: 120, align: 'right',
      render: (s) => String(Object.values(s.confidentialCounts ?? {}).reduce((a, b) => a + b, 0)) },
    { key: 'copied', header: 'Copy to Core', width: 170, render: (s) => <StatusTag status={s.copiedToCoreStatus} /> },
  ]

  return (
    <ListPage title="Submissions">
      <div className="flex items-center flex-shrink-0 bg-background-main" style={{ height: 54, padding: '0 20px', gap: 12, borderBottom: '0.5px solid #4B5563' }}>
        <div style={{ width: 220 }}>
          <Select
            size="small" ariaLabel="Filter by copy state" placeholder="All copy states" value={copied}
            onChange={(v) => {
              const next = new URLSearchParams(params)
              if (v) next.set('copied', v)
              else next.delete('copied')
              setParams(next)
              setPage(1)
            }}
            options={[
              { value: '', label: 'All copy states' },
              { value: 'not_started', label: 'Not started' },
              { value: 'partial', label: 'Partly copied' },
              { value: 'done', label: 'Copied to Core' },
            ]}
          />
        </div>
      </div>
      <div className="flex-1" style={{ overflow: 'hidden' }}>
        {isLoading
          ? <p className="font-mulish text-text-support" style={{ fontSize: 12, padding: 20 }}>Loading…</p>
          : <DataTable columns={columns} rows={data?.items ?? []} rowKey={(s) => s.id} caption="Submissions" emptyMessage="No submissions yet" maxHeight="100%" />}
      </div>
      <Pagination page={page} pageSize={25} total={data?.total ?? 0} onPageChange={setPage} />
    </ListPage>
  )
}
